import {
  AppNode,
  CategoricalNode,
  DeterministicNode,
  DiscreteNode,
  ForecastTableNode,
  GroupNode,
  LagNode,
  NodeType,
  ScenarioTableNode,
  TableNode,
  isNotGroupNode,
  isNotTableNode,
} from "@features/builder/graph/types"
import { addEdgeToNode } from "@features/builder/helpers/add-edge"
import { getChildrenNodes } from "@features/builder/helpers/nodes"
import { removeEdgeFromNode } from "@features/builder/helpers/remove-edge"
import { TKNode } from "@store/services/converter/types"
import logger from "@utils/logger"
import {
  createParentValueCombinations,
  dfCpdToRows,
  getNodeParentPossibleValues,
  getNodePossibleValues,
  rowsToDfCpd,
} from "@utils/node"
import { isEqual } from "lodash"

export const updateParentParamsOfChildNode = <TParent extends AppNode>(
  childNode: AppNode,
  previousParentNode: TParent,
  currentParentNode: TParent,
  allNodes: AppNode[],
  parentLinkedNode?: TKNode | undefined,
): AppNode[] => {
  // groups and table can't have relations with other nodes
  if (!isNotGroupNode(childNode) || !isNotTableNode(childNode)) return [childNode]

  if (previousParentNode.id !== currentParentNode.id) {
    logger.error(
      `Trying to update relation of parent ${previousParentNode.id} with node ${currentParentNode.id} of child node ${childNode.id}`,
    )

    return [childNode]
  }

  // parent have not changed, do nothing
  if (isEqual(previousParentNode, currentParentNode)) return [childNode]

  const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))

  const previousLink =
    "metaNode" in previousParentNode.data ? previousParentNode.data.metaNode.Link : ""
  const updatedLink =
    "metaNode" in currentParentNode.data ? currentParentNode.data.metaNode.Link : ""
  const linkedNodeHasChanged = previousLink !== updatedLink
  const parentTypeHasChanged = previousParentNode.type !== currentParentNode.type

  // reset the connection in case linked node has changed or parent type has changed
  if (linkedNodeHasChanged || parentTypeHasChanged) {
    // make sure to pass old parent node as this is the one that has impact on child nodes, so we know which values should be removed
    allNodesUpdatedMap.set(previousParentNode.id, previousParentNode)
    const updatedNodesDueToEdgeRemoval = removeEdgeFromNode(
      previousParentNode,
      childNode,
      Array.from(allNodesUpdatedMap.values()),
    )
    updatedNodesDueToEdgeRemoval.forEach((node) => allNodesUpdatedMap.set(node.id, node))

    const updatedChild = allNodesUpdatedMap.get(childNode.id)!
    // and now once adding connection back, make sure that parent is the updated version
    allNodesUpdatedMap.set(currentParentNode.id, currentParentNode)
    const updatedNodesDueToEdgeCreation = addEdgeToNode(
      currentParentNode,
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
      parentLinkedNode,
    )
    updatedNodesDueToEdgeCreation.forEach((node) => allNodesUpdatedMap.set(node.id, node))
  } else {
    const previousParentLabel = previousParentNode.data.label ?? ""
    const expression = childNode.data.originalNode.properties.df_cpd.expression?.[0] ?? ""
    const parentInExpression = expression !== "" && expression.includes(`{${previousParentLabel}}`)

    const parentReference = childNode.data.originalNode.properties.df_cpd[previousParentNode.id]

    // does not keep parent reference, no need to do anything
    if (!parentReference && !parentInExpression) return [childNode]

    let updatedChild = structuredClone(childNode)

    // at this point we know that "previousParentNode" and "currentParentNode" are the same node type
    if (
      previousParentNode.type === NodeType.Categorical ||
      previousParentNode.type === NodeType.Discrete
    ) {
      updatedChild = updateChildWithPossibleValues(
        childNode,
        previousParentNode,
        currentParentNode as CategoricalNode | DiscreteNode,
        Array.from(allNodesUpdatedMap.values()),
      )
      allNodesUpdatedMap.set(updatedChild.id, updatedChild)
    }

    // recursively update children nodes
    const grandChildrenNodes = getChildrenNodes(
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
    )
    grandChildrenNodes.forEach((granChildNode) => {
      const updatedNodes = updateParentParamsOfChildNode(
        granChildNode,
        childNode,
        updatedChild,
        Array.from(allNodesUpdatedMap.values()),
      )
      updatedNodes.forEach((updatedNode) => allNodesUpdatedMap.set(updatedNode.id, updatedNode))
    })
  }

  let updatedChild = allNodesUpdatedMap.get(childNode.id)!

  if (updatedChild.type === NodeType.Lag || updatedChild.type === NodeType.Deterministic) {
    updatedChild = updateChildNodeExpression(updatedChild, previousParentNode, currentParentNode)
    allNodesUpdatedMap.set(updatedChild.id, updatedChild)
  }

  const updatedNodes: AppNode[] = []

  // collect changed nodes
  for (const node of allNodes) {
    const updatedNode = allNodesUpdatedMap.get(node.id)
    const hasChanged = !!updatedNode && !isEqual(node, updatedNode)
    if (updatedNode && hasChanged) updatedNodes.push(updatedNode)
  }

  return updatedNodes
}

const updateChildNodeExpression = <TChild extends DeterministicNode | LagNode>(
  childNode: TChild,
  oldParentNode: AppNode,
  newParentNode: AppNode,
): TChild => {
  const oldParentLabel = oldParentNode.data.label
  const newParentLabel = newParentNode.data.label
  const oldPossibleValues = getNodePossibleValues(oldParentNode)
  const newPossibleValues = getNodePossibleValues(newParentNode)

  const expression = childNode.data.originalNode.properties.df_cpd.expression?.[0]

  if (!expression) return childNode
  if (!oldParentLabel || !newParentLabel) return childNode
  if (!expression.includes(`{${oldParentLabel}}`)) return childNode

  let updatedExpression = expression
  const canReplaceValuesByIndex = oldPossibleValues.length === newPossibleValues.length

  if (canReplaceValuesByIndex) {
    for (let index = 0; index < oldPossibleValues.length; index++) {
      const oldValue = oldPossibleValues[index]
      const newValue = newPossibleValues[index]

      if (!oldValue || !newValue || oldValue === newValue) continue
      updatedExpression = updatedExpression.replaceAll(`'${oldValue}'`, `'${newValue}'`)
      updatedExpression = updatedExpression.replaceAll(`"${oldValue}"`, `"${newValue}"`)
    }
  }

  updatedExpression = updatedExpression.replaceAll(`{${oldParentLabel}}`, `{${newParentLabel}}`)

  if (updatedExpression === expression) return childNode

  const updatedChild = structuredClone(childNode)
  updatedChild.data.originalNode.properties.df_cpd.expression = [updatedExpression]
  return updatedChild
}

const updateChildWithPossibleValues = <
  TChild extends Exclude<AppNode, GroupNode | TableNode | ForecastTableNode | ScenarioTableNode>,
>(
  childNode: TChild,
  oldParentNode: DiscreteNode | CategoricalNode,
  newParentNode: DiscreteNode | CategoricalNode,
  allNodes: AppNode[],
): TChild => {
  const oldValues = getNodePossibleValues(oldParentNode)
  const newValues = getNodePossibleValues(newParentNode)

  if (isEqual(oldValues, newValues)) return childNode

  const currentDfCpd = childNode.data.originalNode.properties.df_cpd

  const oldValuesPerParent = getNodeParentPossibleValues(childNode)
  const newValuesPerParent = structuredClone(oldValuesPerParent)
  newValuesPerParent[oldParentNode.id] = newValues

  const oldDfCpdRows = dfCpdToRows(currentDfCpd)

  // nothing to base new rows on
  if (oldDfCpdRows.length === 0) return childNode

  const updatedChildNode = structuredClone(childNode)

  // determine categorical/discrete parent ids (these we will use for cartesian combinations)
  const parentIds = Object.keys(oldValuesPerParent)
  const parentNodes = parentIds
    .map((parentId) => allNodes.find((n) => n.id === parentId))
    .filter(Boolean) as AppNode[]
  const categoricalDiscreteParent = parentNodes.filter(
    ({ type }) => type === NodeType.Categorical || type === NodeType.Discrete,
  ) as (CategoricalNode | DiscreteNode)[]
  const categoricalDiscreteParentIds = categoricalDiscreteParent.map((n) => n.id)

  const oldNewValuesMap = createOldToNewValuesMap(oldValues, newValues)

  const categoricalDiscreteCombinations = createDiscreteCategoricalValuesCombinations(
    childNode,
    categoricalDiscreteParent,
    oldParentNode.id,
    oldValues,
    newValues,
  )

  // A parent can declare an empty state space (e.g. Geometric with ubound below loc + 1),
  // which leaves no combinations to build rows from. Writing them wipes the child's df_cpd,
  // and the "nothing to base new rows on" guard above then blocks every later recovery.
  if (categoricalDiscreteCombinations.length === 0) {
    logger.error(
      `No parent value combinations for child node ${childNode.id}, keeping its df_cpd unchanged`,
    )

    return childNode
  }

  const newRows: Record<string, unknown>[] = []

  // use categorical/discrete parent combinations to generate rows; for non-categorical parents preserve values from best-match rows
  for (const combination of categoricalDiscreteCombinations) {
    // find rows matching this categorical / discrete combination
    const matchingRows = oldDfCpdRows.filter((row) =>
      Object.entries(combination).every(([parentId, value]) => {
        if (parentId === oldParentNode.id) {
          // check if possibly there is old -> new mapping (e.g. rename of category) to presevre rest of values in the row
          const newValue = oldNewValuesMap[String(row[parentId])] ?? row[parentId]
          return newValue === value
        }
        return row[parentId] === value
      }),
    )

    if (matchingRows.length > 0) {
      // preserve all matching rows (preserve order) and update changed parent value via mapping
      matchingRows.forEach((row) => {
        const clonedRow = structuredClone(row)

        if (clonedRow[oldParentNode.id] !== undefined) {
          const mappedValues = oldNewValuesMap[String(row[oldParentNode.id])]
          clonedRow[oldParentNode.id] = mappedValues ?? row[oldParentNode.id]
        }
        newRows.push(clonedRow)
      })
      continue
    }

    // no exact match — find best match by maximum matching categorical values (consider mapping)
    let bestMatchingRow = oldDfCpdRows[0]
    if (!bestMatchingRow) continue

    let bestScore = -1
    for (const row of oldDfCpdRows) {
      let score = 0
      for (const parentId of categoricalDiscreteParentIds) {
        const mappedValue = oldNewValuesMap[String(row[parentId])] ?? row[parentId]
        const compareValue = parentId === oldParentNode.id ? mappedValue : row[parentId]
        if (compareValue === combination[parentId]) score++
      }

      if (score > bestScore) {
        bestScore = score
        bestMatchingRow = row
      }
    }

    const newRow = structuredClone(bestMatchingRow)
    Object.entries(combination).forEach(([pid, val]) => (newRow[pid] = val))
    newRows.push(newRow)
  }

  updatedChildNode.data.originalNode.properties.df_cpd = rowsToDfCpd(newRows)
  // keep expression if it was defined
  if (childNode.data.originalNode.properties.df_cpd.expression) {
    updatedChildNode.data.originalNode.properties.df_cpd.expression =
      childNode.data.originalNode.properties.df_cpd.expression
  }

  if (childNode.data.originalNode.properties.df_cpd.continuous) {
    updatedChildNode.data.originalNode.properties.df_cpd.continuous =
      childNode.data.originalNode.properties.df_cpd.continuous
  }
  return updatedChildNode
}

type OldToNewValuesMap = Record<string, unknown>
const createOldToNewValuesMap = (oldValues: unknown[], newValues: unknown[]): OldToNewValuesMap => {
  const commonValues = oldValues.filter((v) => newValues.includes(v))
  const oldRemainingValues = oldValues.filter((v) => !commonValues.includes(v))
  const newRemainingValues = newValues.filter((v) => !commonValues.includes(v))

  const oldToNewValuesMap: OldToNewValuesMap = {}
  commonValues.forEach((v) => (oldToNewValuesMap[String(v)] = v))

  if (oldRemainingValues.length > 0 && oldRemainingValues.length === newRemainingValues.length) {
    oldRemainingValues.forEach((value, i) => {
      oldToNewValuesMap[String(value)] = newRemainingValues[i]
    })
  }

  return oldToNewValuesMap
}

const createDiscreteCategoricalValuesCombinations = (
  childNode: AppNode,
  categoricalDiscreteParents: (CategoricalNode | DiscreteNode)[],
  changedParentId: string,
  oldValues: unknown[],
  newValues: unknown[],
) => {
  const oldNewValuesMap = createOldToNewValuesMap(oldValues, newValues)
  const categoricalDiscreteValues: Record<string, unknown[]> = {}

  categoricalDiscreteParents.forEach((parentNode) => {
    if (parentNode.id === changedParentId) {
      // preserve ordering from child's existing rows where possible, mapping old values to new names
      // skip old values that were removed (not present in map)
      const valuesToRetain = oldValues
        .map((v) => oldNewValuesMap[String(v)])
        .filter((v) => v !== undefined)
      const remainingNew = newValues.filter((v) => !valuesToRetain.includes(String(v)))
      categoricalDiscreteValues[parentNode.id] = [...valuesToRetain, ...remainingNew]
    } else {
      const childValues = getNodeParentPossibleValues(childNode)[parentNode.id] ?? []
      const declaredValues = getNodePossibleValues(parentNode)
      const remaining = declaredValues.filter((v) => !childValues.includes(v))
      categoricalDiscreteValues[parentNode.id] = [...childValues, ...remaining]
    }
  })

  return createParentValueCombinations(categoricalDiscreteValues)
}
